import React from "react";
import { useForm } from "react-hook-form";
import styles from "./SignIn.module.scss";
import { useUser } from "../../context/UserContext";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import { SignInForm } from "../../types";
import { api } from "../../services/api";
import { ApiError } from "../../services/api/BaseApi";
import { useAlert } from "../../hooks/useAlert";
import PasswordInput from "../../components/PasswordInput/PasswordInput";

const SignIn: React.FC = () => {
  const { login } = useUser();
  const { alert } = useAlert();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInForm>();

  const onSubmit = async (data: SignInForm) => {
    const payload = {
      ...data,
      email: data.email.toLowerCase(),
      password: CryptoJS.SHA256(data.password).toString(),
    };

    try {
      const result = await api.app.login(payload);
      await login(result.user);
      navigate("/");
    } catch (err: any) {
      // Works for our ApiError
      if (err instanceof ApiError) {
        const data = err.data;

        if (err.status === 403 && (data?.code === "EMAIL_NOT_VERIFIED" || data?.requiresEmailVerification)) {
          navigate("/verify-email", { state: { email: data?.email ?? payload.email } });
          return;
        }

        alert({ message: data?.message || err.message || "Something went wrong. Please try again.", type: 'error' });
        return;
      }

      // Fallback
      alert({ message: err?.message || "Something went wrong. Please try again.", type: 'error' });
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.cursive}>Hippocampus Gaming</span>
          <p className={styles.tagline}>Your gaming universe, all in one place</p>
        </div>

        <div className={styles.divider}>Sign in</div>

        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>

          {/* Email */}
          <div className={styles.inputGroup}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email format",
                },
              })}
            />
            {errors.email && (
              <span className={styles.error}>{errors.email.message}</span>
            )}
          </div>

          {/* Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              {...register("password", {
                required: "Password is required",
                minLength: { value: 6, message: "At least 6 characters" },
              })}
            />
            {errors.password && (
              <span className={styles.error}>{errors.password.message}</span>
            )}
            <a href="/forgot-password" className={styles.forgotLink}>
              Forgot password?
            </a>
          </div>

          {/* Submit */}
          <button type="submit" className={styles.button}>
            Login
          </button>
        </form>

        <p className={styles.footer}>
          Don’t have an account? <a href="/signup">Sign Up</a>
        </p>
        <p className={styles.footer}>
          <a href="/privacy">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default SignIn;
